import { A, useParams } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { FleetEpicPanel } from '../components/FleetEpicPanel';
import { PageHeader } from '../components/PageHeader';
import { QueryBoundary } from '../components/QueryBoundary';
import { StopPlanModal } from '../components/StopPlanModal';
import { Toast } from '../components/Toast';
import { Button, buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { orgHref } from '../lib/org';
import { usePlan } from '../lib/plan';
import { createPlanStop, planStopTarget } from '../lib/planStop';
import { planStopMessage } from '../lib/planStopMessage';
import { SESSION_TOKEN } from '../lib/session';
import { createToast } from '../lib/toast';

// Epic (plan) view (#511) — `GET /v1/plans/:planId` rendered as epic → slices
// → task → PR, in dependency order. Deep-linked from the fleet task queue and
// from `/fleet/plans`; a deep route has no nav entry, so the header keeps an
// explicit way back to that index (#298). A missing/foreign plan 404s at the
// API; that lands in the boundary's error slot with the view-specific not-found
// copy rather than the generic failure message. `token` is injectable for tests
// (mirrors Fleet/FleetAudit);
// no session → the query stays idle and the boundary shows the empty state.

export function EpicView(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const params = useParams<{ plan_id: string }>();
  const plan = usePlan(() => params.plan_id ?? null, token);
  const { toast, notify, dismiss } = createToast();
  // Re-READ after a stop: this view is a plain query with no live stream behind
  // it, so nothing else would repaint the slices the write just moved.
  const stop = createPlanStop({
    planId: () => params.plan_id ?? null,
    token,
    onStopped: (result) => {
      void plan.refetch();
      notify(
        planStopMessage(result),
        result.skipped_review_task_ids.length > 0 ? 'error' : 'success',
      );
    },
  });

  const notFound = (): boolean =>
    plan.isError && plan.error instanceof ApiError && plan.error.status === 404;

  return (
    <section class="page epic">
      <PageHeader
        title={t('fleet.epic.title')}
        desc={t('fleet.epic.subtitle')}
        // Lands on the epics INDEX, not `/fleet`: the index exists now, and a
        // back button that skips its own parent leaves the neighbouring epics as
        // unreachable as they were before it shipped.
        actions={
          <>
            {/* ABSENT when every slice has finished — the same rule the queue
                rows follow. The projection carries each slice's task status, so
                the fold is honest rather than a guess. */}
            <Show when={plan.data ? planStopTarget(plan.data) : null}>
              {(target) => (
                <Button variant="destructive" onClick={() => stop.ask(target())}>
                  {t('fleet.stop.plan_action')}
                </Button>
              )}
            </Show>
            <A class={buttonClass({ variant: 'outline' })} href={orgHref('/fleet/plans')}>
              {t('fleet.plans.back')}
            </A>
          </>
        }
      />
      <Show
        when={!notFound()}
        fallback={
          <ErrorState message={t('fleet.epic.not_found')} onRetry={() => void plan.refetch()} />
        }
      >
        <QueryBoundary
          query={plan}
          isEmpty={(p) => p.groups.length === 0}
          loading={<CardGridSkeleton listClass="epic__slices" />}
          empty={<EmptyState message={t('fleet.epic.empty')} />}
        >
          {(projection) => <FleetEpicPanel projection={projection} />}
        </QueryBoundary>
      </Show>
      <StopPlanModal plan={stop.target()} onConfirm={stop.confirm} onClose={stop.close} />
      <Toast message={stop.error()} tone="error" onDismiss={stop.clearError} />
      <Toast
        message={toast()?.message ?? null}
        tone={toast()?.tone ?? 'success'}
        onDismiss={dismiss}
      />
    </section>
  );
}
